/**
 * ResetPasswordPage — set a new password using a reset token.
 *
 * Public route. Reads `?token=...` from the URL.
 * Calls POST /v1/auth/reset-password.
 * On success: redirects to /login?reset=1.
 * On invalid/expired token: shows error state with "Request a new link" CTA.
 */
import { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Divider,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiClient, ApiError } from '@/lib/api-client.js';
import { BrandMark, filledInputSx, outerBoxSx } from './auth-shared.js';

const schema = z
  .object({
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters')
      .max(128, 'Password must be 128 characters or less'),
    confirm_password: z.string(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [submitting, setSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState(!token);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (data) => {
    setSubmitting(true);
    try {
      await apiClient.post('/v1/auth/reset-password', {
        body: { token, password: data.password },
      });
      navigate('/login?reset=1', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && (err.status === 400 || err.status === 401)) {
        setTokenError(true);
      }
    } finally {
      setSubmitting(false);
    }
  });

  if (tokenError) {
    return (
      <Box sx={outerBoxSx}>
        <BrandMark />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
          Link Expired
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 440 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            This reset link is invalid or has expired.
          </Alert>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              Request a new link
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={outerBoxSx}>
      <BrandMark />

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
        Set New Password
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 440 }}>
        <form onSubmit={onSubmit}>
          <Stack spacing={1.5}>
            <TextField
              hiddenLabel
              placeholder="New password"
              type="password"
              autoComplete="new-password"
              variant="filled"
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message ?? '12–128 characters.'}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />

            <TextField
              hiddenLabel
              placeholder="Confirm new password"
              type="password"
              autoComplete="new-password"
              variant="filled"
              {...register('confirm_password')}
              error={Boolean(errors.confirm_password)}
              helperText={errors.confirm_password?.message}
              disabled={submitting}
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                },
              }}
              sx={filledInputSx}
            />
          </Stack>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, borderRadius: 8, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
          >
            Reset Password
          </Button>
        </form>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Back to log in
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
